﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The header used by the customer plaza pages.

import * as React from "react";
import { createUseStyles } from "react-jss";
import { LogoutLink } from "~base/Components/Composite/LogoutLink";
import Button from "~base/Components/Primitives/Button";
import { Icon } from "~base/Components/Primitives/Icon";
import { RouteLink } from "~base/Components/Primitives/Link";
import { useIntl } from "~base/Internationalization/IntlContext";
import { applyThemeStyles } from "~base/Styling";
import { useTransitionStyles } from "~base/Styling/Transitions/TransitionGroup";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import { ConsilioSingleTransition } from "~base/Styling/Transitions/ConsilioSingleTransition";

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface PageHeaderProps {
    parentReducerId?: string;
    constComponentId?: string;
}

//-------------------------------------------------------------------------------------
// Styling
//-------------------------------------------------------------------------------------

const useStyles = createUseStyles(
    (theme: SoPNLConsilioTheme) => ({
        container: {
            display: "flex",
            alignItems: "center",
        },
        // Taken from HeaderMenu styles
        button: applyThemeStyles(
            theme.components.pages.shared.headerMenu.button,
            {
                color: theme.constants.primaryColor,
                display: "flex",
                alignItems: "center",
                gap: "32px",
            }
        ),
        icon: applyThemeStyles(theme.components.pages.shared.headerMenu.icon, {
            width: "22px",
            height: "22px",
        }),
        label: applyThemeStyles(
            theme.components.pages.shared.headerMenu.label,
            {
                fontFamily: theme.constants.defaultFont,
                fontSize: "13px",
                whiteSpace: "nowrap",
            }
        ),
        wrapper: applyThemeStyles(
            theme.components.pages.shared.headerMenu.wrapper,
            {
                position: "relative",
            }
        ),
        menu: applyThemeStyles(theme.components.pages.shared.headerMenu.menu, {
            whiteSpace: "nowrap",
            position: "absolute",
            top: "45px",
            right: "0px",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            zIndex: 10,
        }),
        item: applyThemeStyles(theme.components.pages.shared.headerMenu.item, {
            padding: "10px",
        }),
        arrow: applyThemeStyles(
            theme.components.pages.shared.headerMenu.arrow,
            {
                display: "none",
                width: "8px",
                height: "8px",
                position: "absolute",
                transform: "rotateY(0deg) rotate(45deg)",
                right: "8px",
                top: "-4px",
            }
        ),
        divider: applyThemeStyles(
            theme.components.pages.shared.headerMenu.divider,
            {
                display: "none",
            }
        ),
    }),
    { name: "Menu" }
);

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

const CustomerHeaderMenu: React.FunctionComponent<PageHeaderProps> = ({
    parentReducerId,
    constComponentId,
}) => {
    const classes = useStyles();
    const intl = useIntl();

    const key = React.useRef<HTMLDivElement>(undefined);

    const [state, dispatch, distinguishId] = useConsilioReducer(
        buildReducerId("HeaderMenu", parentReducerId, constComponentId),
        HeaderMenuReducer,
        { open: false },
        false
    );

    React.useEffect(() => {
        const closeOnClickHandler = (ev: MouseEvent) => {
            if (key.current && state.open) {
                if (!key.current.contains(ev.target as unknown as Node)) {
                    dispatch({ type: "toggleMenu", open: false });
                }
            }
        };

        window.addEventListener("click", closeOnClickHandler);

        return () => {
            window.removeEventListener("click", closeOnClickHandler);
        };
    }, [key, dispatch, state.open]);

    const [fadeClasses, timeout] = useTransitionStyles("FadeInFast", false);

    return (
        <div className={classes.container}>
            <div ref={key} className={classes.wrapper}>
                <Button
                    testIdentifier="ToggleMenu"
                    styleType={"Transparent"}
                    buttonWrapperClass={classes.button}
                    onClick={() => {
                        dispatch({ type: "toggleMenu", open: !state.open });
                    }}
                >
                    <Icon
                        iconFQN="Consilio.UI.Icons.Menu.Hamburger"
                        iconStyleType="Control"
                        svgClassName={classes.icon}
                    />
                </Button>
                <ConsilioSingleTransition
                    active={state.open}
                    transitionClassNames={fadeClasses}
                    timeout={timeout}
                >
                    <div className={classes.menu}>
                        <div className={classes.arrow} />
                        <RouteLink
                            testIdentifier="Start"
                            to="/Home/Start"
                            textCode="SoPNL.UI.CustomerStartPage.Menu.Start"
                            className={classes.item}
                        />
                        <RouteLink
                            testIdentifier="Settings"
                            to="/Home/Settings"
                            textCode="SoPNL.UI.CustomerStartPage.Menu.Settings"
                            className={classes.item}
                        />
                        <LogoutLink
                            testIdentifier="Logout"
                            className={classes.item}
                        >
                            {intl.formatText("VisiFinance.UI.Main.LoggaUt")}
                        </LogoutLink>
                    </div>
                </ConsilioSingleTransition>
            </div>
        </div>
    );
};

interface HeaderMenuState {
    open: boolean;
}

type ToggleAction = { type: "toggleMenu"; open: boolean };

type HeaderMenuViewAction = ToggleAction;

const HeaderMenuReducer = (
    state: HeaderMenuState,
    action: HeaderMenuViewAction
): HeaderMenuState => {
    switch (action.type) {
        case "toggleMenu": {
            return {
                ...state,
                open: action.open,
            };
        }
        default:
            return state;
    }
};

export default CustomerHeaderMenu;
export { CustomerHeaderMenu };
